import React, { useEffect, useState, useRef } from 'react';

import { makeStyles } from '@material-ui/core';
import { showConfirmCohortBaseResetModalAction } from 'actions/cohortBaseResetModal';
import SkeletonLoader from 'components/SkeletonLoader';
import { useDispatch, useSelector } from 'react-redux';
import { COHORT_RESET_MODAL_TYPES } from 'reducers/cohortBaseResetModal';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { StyledBox, StyledTypography } from 'styles/common';
import { checkRoleAccess } from 'utils/helpers/rolesAccess';

import theme from 'constants/theme';

import { getOrganizationTags } from 'services/admin';

import { DateRange, ExpandedView } from './CohortSidePanel';
import { useCohortCreationContext } from './CohortSidePanelContext';
import { CohortCreationSelect, DropdownContainer, RangeSelector } from './CommonComponents';

const MODULE_NAME = 'feedback';

const useStyles = makeStyles({
  paper: {
    boxShadow: '3px 4px 5px 0px #0000001C',
    border: `1px solid ${theme.palette.borders.lighterGrey}`,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
});

export const onClickPreCheck = (
  // to add enabledFeatures
  dispatch,
  updateBaseCohort,
  callBackFunc,
  accessibleRole,
  isAllEmployeesSelected,
  moduleName,
  enabledFeatures
) => {
  // if user has admin access then let him have normal flow
  if (!checkRoleAccess(accessibleRole, enabledFeatures) && isAllEmployeesSelected) {
    // open a confirmation modal, if user click yes then go ahead
    // with setting base as "My org" and then normal flow

    dispatch(
      showConfirmCohortBaseResetModalAction(
        () => {
          updateBaseCohort('-3');
          callBackFunc();
        },
        () => {},
        COHORT_RESET_MODAL_TYPES.noAdminAccessModal,
        moduleName
      )
    );
    return;
  }

  callBackFunc();
};

const FeedbackCompetencies = ({ onClick, accessibleRole, isAllEmployeesSelected, moduleName }) => {
  const [loading, setLoading] = useState(true);
  const [competencies, setCompetenciess] = useState([]);
  const [selectedCompetency, setSelectedCompetency] = useState('');
  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);

  const getData = () => {
    getOrganizationTags({ teamId: -1, tagType: 'CORE_COMPETENCIES' })
      .then((orgTags) => {
        const activeTags = orgTags.filter((tag) => tag.active);
        const competencies = activeTags.map((tag) => ({
          key: tag.id,
          displayName: tag.displayName,
        }));
        setCompetenciess(competencies);
      })
      .finally(() => setLoading(false));
  };

  // get tags
  useEffect(() => {
    setLoading(true);
    getData();
  }, []);

  const dispatch = useDispatch();

  const { updateBaseCohort } = useCohortCreationContext();

  useEffect(() => {
    onClick(selectedCompetency);
  }, [selectedCompetency]);

  const setSelectedCompetencyCurried = (target) => () => {
    setSelectedCompetency(target);
  };

  if (loading) {
    return <SkeletonLoader skeletons={[100, 100]} />;
  }

  return (
    <StyledBox height="2.5rem" display="flex" alignItems="center" justifyContent="space-between">
      <StyledTypography variant="body2" color="textSecondary" rightGutter={2}>
        Competency
      </StyledTypography>
      <CohortCreationSelect
        options={competencies}
        selectedOption={selectedCompetency}
        onChange={(competency) =>
          onClickPreCheck(
            dispatch,
            updateBaseCohort,
            setSelectedCompetencyCurried(competency),
            accessibleRole,
            isAllEmployeesSelected,
            moduleName,
            enabledFeatures
          )
        }
        defaultOptionText="Select Competency"
      />
    </StyledBox>
  );
};

function CohortCreationFeedback({
  content,
  sectionLabel,
  sectionId,
  onHeaderClick,
  accessibleRole,
  isAllEmployeesSelected,
}) {
  const dispatch = useDispatch();
  const { updateBaseCohort } = useCohortCreationContext();

  const latestValue = useRef(isAllEmployeesSelected);

  useEffect(() => {
    latestValue.current = isAllEmployeesSelected;
  }, [isAllEmployeesSelected]);

  return (
    <>
      <ExpandedView
        content={content}
        sectionLabel={sectionLabel}
        sectionId={sectionId}
        headerComponent={() => (
          <DateRange
            sectionId={sectionId}
            onHeaderClick={onHeaderClick}
            goalTextLabel="Timeline"
            isAllEmployeesSelected={isAllEmployeesSelected}
            accessibleRole={accessibleRole}
            moduleName={MODULE_NAME}
          />
        )}
      />
      <DropdownContainer>
        <FeedbackCompetencies
          onClick={(selectedValue) => onHeaderClick(sectionId, 'competencies', selectedValue)}
          isAllEmployeesSelected={isAllEmployeesSelected}
          accessibleRole={accessibleRole}
          moduleName={MODULE_NAME}
        />
      </DropdownContainer>

      <DropdownContainer topGutter={3}>
        <RangeSelector
          dataKey="data"
          sectionId={sectionId}
          headingText="Score (Range)"
          isAllEmployeesSelected={isAllEmployeesSelected}
          accessibleRole={accessibleRole}
          moduleName={MODULE_NAME}
        />
      </DropdownContainer>
    </>
  );
}

export default CohortCreationFeedback;
